/* PBW: cart quantity messages and the 24 bottle order limit.
   1) Shopify answers "Only N items were added to your cart due to availability." when a line is at its
      per order limit. Say it plainly instead.
   2) The theme leaves an old line error on screen after a later change succeeds. Clear it when the cart redraws.
   3) Orders are limited to MAX bottles. Any add to cart, or any quantity increase, that would take the cart
      above the limit is stopped here with a clear message. On a wine page the plus button stops at the limit
      straight away. Checkout is also switched off in cart-summary.liquid, and kept switched off here
      when the cart redraws (the theme redraw can leave the button active).
      The same number is set in cart-summary.liquid and bmsm-bottle-nudge.liquid. */
const MAX = 24;
const MAX_RE = /^Only (\d+) items? were added to your cart due to availability\.?$/;
const ERR = '[ref^="cartItemError-"]';
const BOX = '[ref^="cartItemErrorContainer-"]';
const CHECKOUT = '.cart__checkout-button, button[name="checkout"]';

const elementOf = (node) => (node && node.nodeType === 1 ? node : node && node.parentElement);

if (!window.__pbwCartLimits) {
  window.__pbwCartLimits = true;

  new MutationObserver((records) => {
    let touched = false;
    let inError = false;
    for (const record of records) {
      const el = elementOf(record.target);
      if (!el || !el.closest('cart-items-component')) continue;
      touched = true;
      if (el.closest(ERR)) inError = true;
    }
    if (!touched) return;

    if (inError) {
      document.querySelectorAll(ERR).forEach((el) => {
        const match = MAX_RE.exec(el.textContent.trim());
        if (match) el.textContent = 'The maximum for this wine is ' + match[1] + ' bottles per order.';
      });
      return;
    }

    document.querySelectorAll(BOX).forEach((el) => el.classList.add('hidden'));
  }).observe(document.body, { childList: true, subtree: true, characterData: true });

  const originalFetch = window.fetch.bind(window);

  const bottlesIn = (cart) =>
    cart.items.filter((item) => !item.gift_card).reduce((sum, item) => sum + item.quantity, 0);

  const readCart = async () => {
    const response = await originalFetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    return response.json();
  };

  /* Checkout stays off while the over the limit message is showing. The message is drawn by the same
     server render as the button, so it is the reliable sign of the real state. */
  const syncCheckout = () => {
    const over = !!document.querySelector('.cart__limit-warning');
    document.querySelectorAll(CHECKOUT).forEach((button) => {
      if (over) {
        if (!button.disabled) {
          button.disabled = true;
          button.dataset.pbwLocked = '1';
        }
      } else if (button.dataset.pbwLocked) {
        button.disabled = false;
        delete button.dataset.pbwLocked;
      }
    });
  };
  let syncQueued = false;
  const scheduleSync = () => {
    if (syncQueued) return;
    syncQueued = true;
    requestAnimationFrame(() => {
      syncQueued = false;
      syncCheckout();
    });
  };
  new MutationObserver(scheduleSync).observe(document.body, { childList: true, subtree: true });
  scheduleSync();

  /* Bottles in the cart as last seen, so the plus button can be checked without waiting */
  let knownBottles = null;
  const refreshKnown = () =>
    readCart()
      .then((cart) => {
        knownBottles = bottlesIn(cart);
        scheduleSync();
      })
      .catch(() => {});
  refreshKnown();
  window.addEventListener('pageshow', refreshKnown);

  const bottlesAdded = (body) => {
    try {
      if (body instanceof FormData) {
        let total = 0;
        let found = false;
        for (const [key, value] of body.entries()) {
          if (key === 'quantity' || /^items\[\d+\]\[quantity\]$/.test(key)) {
            total += parseInt(value, 10) || 0;
            found = true;
          }
        }
        return found ? total : 1;
      }
      if (typeof body === 'string') {
        const data = JSON.parse(body);
        if (Array.isArray(data.items)) return data.items.reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);
        return Number(data.quantity) || 1;
      }
    } catch (error) {
      /* fall through */
    }
    return 1;
  };

  const messageFor = (have) => {
    if (have >= MAX) return 'You have reached the maximum of ' + MAX + ' bottles per order.';
    const room = MAX - have;
    return (
      'Orders are limited to ' + MAX + ' bottles. You have ' + have + ' in your basket, so you can add ' +
      room + ' more ' + (room === 1 ? 'bottle' : 'bottles') + '.'
    );
  };

  let toastTimer;
  const hideToast = () => {
    const el = document.getElementById('pbw-limit-toast');
    if (el) el.style.display = 'none';
  };
  const toast = (text) => {
    let el = document.getElementById('pbw-limit-toast');
    if (!el) {
      el = document.createElement('div');
      el.id = 'pbw-limit-toast';
      el.setAttribute('role', 'alert');
      el.style.cssText =
        'position:fixed;left:50%;bottom:80px;transform:translateX(-50%);z-index:2147483000;max-width:min(92vw,420px);' +
        'padding:12px 16px;border-radius:10px;background:#fdecee;color:#b3261e;font-family:inherit;font-size:14px;' +
        'font-weight:600;line-height:1.35;box-shadow:0 6px 24px rgba(0,0,0,.18);text-align:center;';
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.style.display = 'block';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 5000);
  };

  /* After a refused add, the wine page shows the same sentence under the price. Then the pop-up is not needed. */
  const inlineShows = (text) =>
    [...document.querySelectorAll('body *')].some(
      (el) =>
        el.children.length === 0 &&
        el.textContent.trim() === text &&
        el.offsetParent !== null &&
        !el.closest('#pbw-limit-toast, dialog')
    );

  const refuse = (text) =>
    new Response(JSON.stringify({ status: 422, message: text, description: text, errors: text }), {
      status: 422,
      headers: { 'Content-Type': 'application/json' },
    });

  window.fetch = async function (input, init) {
    try {
      const url = typeof input === 'string' ? input : input && input.url;
      const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
      const match = url && method === 'POST' ? /\/cart\/(add|change|update)(\.js)?(\?|$)/.exec(url) : null;

      if (match && init && init.body && match[1] !== 'update') {
        const cart = await readCart();
        const have = bottlesIn(cart);
        knownBottles = have;

        if (match[1] === 'add') {
          const adding = bottlesAdded(init.body);
          if (have + adding > MAX) {
            const text = messageFor(have);
            toast(text);
            setTimeout(() => {
              if (inlineShows(text)) hideToast();
            }, 700);
            return refuse(text);
          }
        } else if (typeof init.body === 'string') {
          const data = JSON.parse(init.body);
          const item = data.line ? cart.items[Number(data.line) - 1] : cart.items.find((i) => i.key === data.id);
          if (item && !item.gift_card) {
            const increase = Number(data.quantity) - item.quantity;
            if (increase > 0 && have + increase > MAX) {
              const text = messageFor(have);
              toast(text);
              return refuse(text);
            }
          }
        }
      }

      if (match) {
        const response = await originalFetch(input, init);
        refreshKnown();
        return response;
      }
    } catch (error) {
      console.warn('[pbw-cart-limits]', error);
    }
    return originalFetch(input, init);
  };

  /* Checkout buttons do nothing while the cart is over the limit, even if a redraw left one active */
  document.addEventListener(
    'click',
    (event) => {
      const el = elementOf(event.target);
      const button = el && el.closest(CHECKOUT + ', a[href="/checkout"]');
      if (!button) return;
      const over = document.querySelector('.cart__limit-warning') || (knownBottles !== null && knownBottles > MAX);
      if (!over) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const extra = knownBottles !== null && knownBottles > MAX ? knownBottles - MAX : 1;
      toast('Orders are limited to ' + MAX + ' bottles. Please remove ' + extra + (extra === 1 ? ' bottle' : ' bottles') + ' to check out.');
      syncCheckout();
    },
    true
  );

  /* Wine page: the plus button stops at the limit and says so straight away */
  const productSelector = (node) => {
    const el = elementOf(node);
    const selector = el && el.closest('quantity-selector-component');
    return selector && !selector.closest('cart-items-component') ? selector : null;
  };

  /* Bring the number back to what still fits. The theme may already have added one by then. */
  const clampToRoom = (input) => {
    if (knownBottles === null) return;
    const wanted = parseInt(input.value, 10) || 1;
    if (knownBottles + wanted > MAX) {
      input.value = String(Math.max(1, MAX - knownBottles));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const plus = elementOf(event.target) && elementOf(event.target).closest('button[name="plus"]');
      if (!plus || knownBottles === null) return;
      const selector = productSelector(plus);
      const input = selector && selector.querySelector('input');
      if (!input) return;

      const wanted = (parseInt(input.value, 10) || 1) + 1;
      if (knownBottles + wanted > MAX) {
        event.preventDefault();
        event.stopImmediatePropagation();
        toast(messageFor(knownBottles));
        setTimeout(() => clampToRoom(input), 50);
      }
    },
    true
  );

  /* Typing a number above the room left brings it back to what fits */
  document.addEventListener(
    'change',
    (event) => {
      const target = elementOf(event.target);
      if (!target || target.tagName !== 'INPUT' || knownBottles === null) return;
      if (!productSelector(target)) return;

      const wanted = parseInt(target.value, 10) || 1;
      if (knownBottles + wanted > MAX) {
        target.value = String(Math.max(1, MAX - knownBottles));
        toast(messageFor(knownBottles));
      }
    },
    true
  );
}
